import type { LocationQuery } from 'vue-router'
import {
  Settings,
  Play,
  Clock,
  Repeat,
  Database,
  History,
  SlidersHorizontal
} from 'lucide-vue-next'
import { buildScopedListQuery } from '@/shared/utils/scopedContext'

export const AUTOMATION_ACCOUNT_QUERY_KEY = 'account_id' as const
export const AUTOMATION_LIST_ROUTE_NAME = 'uc-automations' as const

export function buildAutomationListQuery(
  accountId?: string | number | null,
  routeQuery?: LocationQuery
) {
  return buildScopedListQuery({ accountId }, routeQuery)
}

export function buildAutomationListRoute(
  accountId?: string | number | null,
  routeQuery?: LocationQuery
) {
  return {
    name: AUTOMATION_LIST_ROUTE_NAME,
    query: buildAutomationListQuery(accountId, routeQuery)
  }
}

export function buildAutomationEditRoute(
  automationId: string | number,
  accountId?: string | number | null,
  routeQuery?: LocationQuery
) {
  return {
    name: 'uc-automation-edit' as const,
    params: { id: String(automationId) },
    query: buildAutomationListQuery(accountId, routeQuery)
  }
}

/** Must match backend AutomationAction::type_options / DB enum values. */
export const AUTOMATION_ACTION_TYPE = {
  EMAIL: 'email',
  SMS: 'sms',
  TWO_WAY_TOGGLE: 'two-way-toggle-state',
  MULTIWAY_TOGGLE: 'multiway-toggle-dest',
  DYNAMIC_CALLER_ID: 'dynamic-caller-id',
  CALL_QUEUE_LOGIN: 'call-queue-login',
  CALL_QUEUE_LOGOUT: 'call-queue-logout'
} as const

export type AutomationActionType =
  (typeof AUTOMATION_ACTION_TYPE)[keyof typeof AUTOMATION_ACTION_TYPE]

export const SMS_DESTINATION_TYPE = {
  CALLER: 'caller',
  STATIC: 'static'
} as const

export const TWO_WAY_TOGGLE_STATE_OPTIONS = [
  { label: 'Enabled', value: '1' },
  { label: 'Disabled', value: '0' }
] as const

export const SMS_DESTINATION_OPTIONS = [
  { label: "Caller's #", value: SMS_DESTINATION_TYPE.CALLER },
  { label: 'Static #', value: SMS_DESTINATION_TYPE.STATIC }
] as const

/** Must match backend AutomationRecurringRun::frequency_options. */
export const RECURRING_RUN_FREQUENCY = {
  HOURLY: 'hourly',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly'
} as const

export type RecurringRunFrequency =
  (typeof RECURRING_RUN_FREQUENCY)[keyof typeof RECURRING_RUN_FREQUENCY]

export const RECURRING_RUN_FREQUENCY_OPTIONS = [
  { label: 'Hourly', value: RECURRING_RUN_FREQUENCY.HOURLY },
  { label: 'Daily', value: RECURRING_RUN_FREQUENCY.DAILY },
  { label: 'Weekly', value: RECURRING_RUN_FREQUENCY.WEEKLY },
  { label: 'Monthly', value: RECURRING_RUN_FREQUENCY.MONTHLY }
] as const

/** 1 = Sunday … 7 = Saturday — matches legacy AutomationRecurringRun::weekday_options. */
export const RECURRING_RUN_WEEKDAY_OPTIONS = [
  { label: 'Sunday', value: '1' },
  { label: 'Monday', value: '2' },
  { label: 'Tuesday', value: '3' },
  { label: 'Wednesday', value: '4' },
  { label: 'Thursday', value: '5' },
  { label: 'Friday', value: '6' },
  { label: 'Saturday', value: '7' }
] as const

export const RECURRING_RUN_MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => ({
  label: String(index + 1),
  value: String(index + 1)
}))

export const automation_tabs = [
  { id: 'edit', label: 'Edit', description: 'Basic automation settings', icon: Settings },
  { id: 'actions', label: 'Actions', description: 'Configure automation actions', icon: Play },
  {
    id: 'one-time-runs',
    label: 'One-Time Runs',
    description: 'Single execution history',
    icon: Clock
  },
  {
    id: 'recurring-runs',
    label: 'Recurring Runs',
    description: 'Recurring schedules',
    icon: Repeat
  },
  {
    id: 'advanced',
    label: 'Advanced',
    description: 'Configure advanced settings',
    icon: SlidersHorizontal
  },
  {
    id: 'dependents',
    label: 'Dependencies',
    description: 'Where this resource is used',
    icon: Database
  },
  {
    id: 'audits',
    label: 'Audit Logs',
    description: 'Track changes and modifications',
    icon: History
  }
]
